import cx from 'classnames'
import { useRef, useState, forwardRef, useImperativeHandle, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { stripePromise } from 'components/UpdatePaymentMethodModal/UpdatePaymentMethodModal'
import { CardholderNameInput } from 'screens/LoginOrSignup/components/Blaze/StartFreeTrial/components/FreeTrialCardForm/FreeTrialCardForm'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useRootStore } from 'stores/RootStore'
import { ArrowLeft, Card } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'

import Styles from './StripePaymentForm.module.scss'
import CreditCardFormStyles from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/VirtualMarketerPlansModal/components/CreditCardForm/CreditCardForm.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Skeleton } from 'antd'

const LoadingSkeleton = () => {
  return (
    <div className={Styles.loadingSkeleton}>
      <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 1, width: '100%' }} />
    </div>
  )
}

export interface StripePaymentFormRef {
  submitPayment: () => Promise<void>
  hasErrors: () => boolean
}

interface PaymentFormInnerProps {
  onPaymentMethodCreated: (paymentMethodId?: string) => void
  onError?: (error: string) => void
  onErrorChange?: (hasError: boolean) => void
  isProcessing: boolean
  setIsProcessing: (processing: boolean) => void
  showCardholderName?: boolean
  disabled?: boolean
}

const PaymentFormInner = observer(
  forwardRef<{ handlePaymentSubmission: () => Promise<void>; hasErrors: () => boolean }, PaymentFormInnerProps>(
    (
      {
        onPaymentMethodCreated,
        onError,
        onErrorChange,
        isProcessing,
        setIsProcessing,
        showCardholderName = true,
        disabled = false,
      },
      ref,
    ) => {
      const stripe = useStripe()
      const elements = useElements()
      const [ready, setReady] = useState(false)
      const [isCardHolderEmpty, setIsCardHolderEmpty] = useState(false)
      const [isCardPayment, setIsCardPayment] = useState(true)
      const [showNewCardForm, setShowNewCardForm] = useState(false)
      const [hasPaymentError, setHasPaymentError] = useState(false)
      const cardHolderNameRef = useRef<HTMLInputElement>(null)

      const { currentSubscription } = useRootStore().tierUsageStore
      const hasExistingCard = currentSubscription?.card?.last_four

      const handlePaymentSubmission = async () => {
        // If user has existing card and hasn't switched to new card form, use existing card
        if (hasExistingCard && !showNewCardForm) {
          // Just call the success callback with a placeholder - the parent should handle existing card logic
          onPaymentMethodCreated()
          return
        }

        if (!stripe || !elements) {
          onError?.('Stripe not initialized')
          return
        }

        setIsProcessing(true)

        try {
          // Validate cardholder name if using card payment and field is shown
          const cardHolderName = cardHolderNameRef.current?.value || ''
          if (showCardholderName && isCardPayment && cardHolderName === '') {
            setIsCardHolderEmpty(true)
            setIsProcessing(false)
            return
          }

          const { setupIntent, error: setupError } = await stripe.confirmSetup({
            elements,
            confirmParams: {
              // eslint-disable-next-line no-restricted-properties
              return_url: window.location.href,
              payment_method_data: showCardholderName
                ? {
                    billing_details: { name: cardHolderName },
                  }
                : undefined,
            },
            redirect: 'if_required',
          })

          if (setupError || !setupIntent?.payment_method) {
            const errorMessage = setupError?.message || 'Payment method not found. Try again.'
            showToastMessage(errorMessage, 'error')
            onError?.(errorMessage)
            setIsProcessing(false)
            return
          }

          const paymentMethodId = setupIntent.payment_method as string
          onPaymentMethodCreated(paymentMethodId)
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
          showToastMessage(errorMessage, 'error')
          onError?.(errorMessage)
          setIsProcessing(false)
        }
      }

      // Listen for cardholder name input changes to propagate errors
      useEffect(() => {
        const inputElement = cardHolderNameRef.current
        if (!inputElement || !showCardholderName) return

        const handleInput = () => {
          // Use setTimeout to ensure React's state updates before reading ref value
          setTimeout(() => {
            const cardHolderNameValue = cardHolderNameRef.current?.value || ''
            const hasCardHolderNameError = isCardPayment && cardHolderNameValue.trim() === ''
            onErrorChange?.(hasPaymentError || hasCardHolderNameError)
          }, 0)
        }

        inputElement.addEventListener('input', handleInput)

        // Also call once on mount to set initial state
        handleInput()

        return () => inputElement.removeEventListener('input', handleInput)
      }, [hasPaymentError, isCardPayment, showCardholderName, onErrorChange])

      useImperativeHandle(ref, () => ({
        handlePaymentSubmission,
        hasErrors: () => {
          // If user has existing card and hasn't switched to new card form, no errors
          if (hasExistingCard && !showNewCardForm) {
            return false
          }
          // Check if Stripe is ready and if there are payment errors
          const cardHolderNameValue = cardHolderNameRef.current?.value || ''
          const hasCardHolderNameError = showCardholderName && isCardPayment && cardHolderNameValue.trim() === ''
          return !ready || hasPaymentError || hasCardHolderNameError
        },
      }))

      return (
        <div className={cx(Styles.paymentFormInner, { [Styles.existingCard]: hasExistingCard && !showNewCardForm })}>
          {hasExistingCard && !showNewCardForm ? (
            <div className={CreditCardFormStyles.cardOnFileContainer}>
              <Text variant="label" color="var(--dark-60)">
                Saved payment method
              </Text>
              <div className={CreditCardFormStyles.cardOnFile}>
                <div className={CreditCardFormStyles.cardDetails}>
                  <Card size={20} />
                  <div className={CreditCardFormStyles.cardNumber}>
                    <p className={CreditCardFormStyles.obscured}>•••• •••• ••••</p>
                    <p>{currentSubscription.card.last_four}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onPress={() => setShowNewCardForm(true)}>
                  Change
                </Button>
              </div>
            </div>
          ) : (
            <>
              {hasExistingCard && (
                <div className={Styles.backToExisting}>
                  <Button frontIcon={ArrowLeft} variant="ghost" size="sm" onPress={() => setShowNewCardForm(false)}>
                    Use Existing Card
                  </Button>
                </div>
              )}
              {showCardholderName && (
                <CardholderNameInput
                  ref={cardHolderNameRef}
                  isInvalid={isCardHolderEmpty}
                  isDisabled={!ready || disabled}
                  setIsInvalid={setIsCardHolderEmpty}
                />
              )}
              <PaymentElement
                onReady={() => setReady(true)}
                options={{
                  paymentMethodOrder: ['card', 'apple_pay', 'google_pay', 'cashapp'],
                  fields: {
                    billingDetails: {
                      name: 'never', // Disable Stripe's name field, we use our own
                    },
                  },
                }}
                onChange={(e) => {
                  const selectedPaymentMethod = e?.value?.type
                  setIsCardPayment(selectedPaymentMethod === 'card')
                  const hasError = !e.complete
                  setHasPaymentError(hasError)
                  onErrorChange?.(hasError)
                }}
              />
            </>
          )}
        </div>
      )
    },
  ),
)

export interface StripePaymentFormProps {
  clientSecret?: string
  onPaymentMethodCreated: (paymentMethodId?: string) => void
  onError?: (error: string) => void
  onErrorChange?: (hasError: boolean) => void
  isProcessing?: boolean
  setIsProcessing?: (processing: boolean) => void
  showCardholderName?: boolean
  disabled?: boolean
  stripeAppearance?: any
  children?: React.ReactNode
}

export const StripePaymentForm = forwardRef<StripePaymentFormRef, StripePaymentFormProps>(
  (
    {
      clientSecret,
      onPaymentMethodCreated,
      onError,
      onErrorChange,
      isProcessing = false,
      setIsProcessing,
      showCardholderName = true,
      disabled = false,
      stripeAppearance,
      children,
    },
    ref,
  ) => {
    const [internalProcessing, setInternalProcessing] = useState(false)
    const paymentFormRef = useRef<{ handlePaymentSubmission: () => Promise<void>; hasErrors: () => boolean }>(null)

    const processing = isProcessing || internalProcessing
    const setProcessing = setIsProcessing || setInternalProcessing

    const defaultAppearance = {
      theme: 'stripe' as const,
      variables: {
        colorPrimary: '#6159FF',
        colorBackground: '#ffffff',
        colorText: '#1A1523',
      },
    }

    useImperativeHandle(ref, () => ({
      submitPayment: async () => {
        if (paymentFormRef.current) {
          await paymentFormRef.current.handlePaymentSubmission()
        }
      },
      hasErrors: () => {
        return paymentFormRef.current?.hasErrors() ?? true
      },
    }))

    if (!clientSecret) {
      return <LoadingSkeleton />
    }

    return (
      <div className={Styles.stripePaymentForm}>
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: stripeAppearance || defaultAppearance,
          }}
        >
          <PaymentFormInner
            ref={paymentFormRef}
            onPaymentMethodCreated={onPaymentMethodCreated}
            onError={onError}
            onErrorChange={onErrorChange}
            isProcessing={processing}
            setIsProcessing={setProcessing}
            showCardholderName={showCardholderName}
            disabled={disabled}
          />
          {children}
        </Elements>
      </div>
    )
  },
)

StripePaymentForm.displayName = 'StripePaymentForm'
